const instances = new WeakMap();
export function uniqueCards(cards) {
  const ids = new Set(), titles = new Set(), photos = new Set();
  return cards.filter(card => {
    const image = card.querySelector('img');
    if (!image) return false;
    const id = card.dataset.productId;
    const title = (card.dataset.cardKey || '').trim().toLowerCase();
    const photo = (image.getAttribute('src') || '').split('?')[0];
    if ((id && ids.has(id)) || (title && titles.has(title)) || (photo && photos.has(photo))) return false;
    if (id) ids.add(id);
    if (title) titles.add(title);
    if (photo) photos.add(photo);
    return true;
  });
}
export function shuffle(cards, random = Math.random) {
  const result = cards.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
export function splitPool(cards, width) {
  const perRow = Math.max(8, Math.ceil((width + 180) / 150) + 2);
  const sample = cards.slice(0, perRow * 2);
  return [sample.filter((_, index) => index % 2 === 0), sample.filter((_, index) => index % 2 === 1)];
}
export class Banner {
  constructor(element) {
    this.element = element;
    this.tracks = [...element.querySelectorAll('.bb-listing-track')];
    this.events = new AbortController();
    this.pool = shuffle(uniqueCards([...element.querySelectorAll('.bb-listing-item')]));
    this.resize = new ResizeObserver(() => this.fill());
    this.resize.observe(element);
    this.inView = true;
    if ('IntersectionObserver' in window) {
      this.visible = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.motion(); });
      this.visible.observe(element);
    }
    document.addEventListener('visibilitychange', () => this.motion(), { signal: this.events.signal });
    this.fill();
    this.load();
  }
  async load() {
    try {
      const url = new URL(this.element.dataset.wallSource, location.origin);
      if (url.origin !== location.origin || !/\/collections\//.test(url.pathname)) return;
      url.searchParams.set('section_id', 'bb-wall-feed');
      url.searchParams.set('filter.v.availability', '1');
      url.searchParams.set('sort_by', 'created-descending');
      const read = async target => {
        const response = await fetch(target.href, { credentials: 'same-origin', signal: this.events.signal });
        if (!response.ok) throw new Error('Banner cards unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = parsed.querySelector('[data-bb-wall-feed]');
        if (!feed) throw new Error('Banner cards unavailable');
        return { cards: [...feed.querySelectorAll('.bb-listing-item')], pages: Number(feed.dataset.pages) };
      };
      const first = await read(url);
      let cards = first.cards;
      if (first.pages > 1) {
        const sampleURL = new URL(url);
        sampleURL.searchParams.set('page', String(2 + Math.floor(Math.random() * (first.pages - 1))));
        try { cards = cards.concat((await read(sampleURL)).cards); } catch { /* Keep the first complete image sample. */ }
      }
      if (this.events.signal.aborted) return;
      const pool = uniqueCards(cards);
      if (pool.length) { this.pool = shuffle(pool); this.fill(); }
    } catch { /* Keep the server-rendered selection and existing motion controls. */ }
  }
  fill() {
    if (!this.element.clientWidth || !this.pool.length) return;
    if (this.lastWidth === this.element.clientWidth && this.lastPool === this.pool) return;
    this.lastWidth = this.element.clientWidth; this.lastPool = this.pool;
    const rows = splitPool(this.pool, this.element.clientWidth);
    this.tracks.forEach((track, index) => {
      const row = rows[index].length ? rows[index] : this.pool;
      const group = document.createElement('div');
      group.className = 'bb-listing-group';
      group.dataset.bbWallGroup = '';
      row.forEach(card => group.append(card.cloneNode(true)));
      track.replaceChildren(group);
      // Only a genuinely short inventory needs repeats inside a visible-width group.
      let repeat = 0;
      while (group.getBoundingClientRect().width < this.element.clientWidth + 180 && repeat < 80) {
        row.forEach(card => group.append(card.cloneNode(true)));
        repeat++;
      }
      track.style.animationDuration = Math.max(55, group.getBoundingClientRect().width / (index === 0 ? 24 : 21)) + 's';
      track.append(group.cloneNode(true));
    });
  }
  motion() { this.tracks.forEach(track => { track.style.animationPlayState = this.inView && !document.hidden ? '' : 'paused'; }); }
  destroy() { this.events.abort(); this.resize.disconnect(); this.visible?.disconnect(); }
}
function initialise(root = document) {
  root.querySelectorAll('[data-bb-wall]').forEach(element => { if (!instances.has(element)) instances.set(element, new Banner(element)); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
else initialise();
document.addEventListener('shopify:section:load', event => initialise(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-bb-wall]').forEach(element => { instances.get(element)?.destroy(); instances.delete(element); }));
